import { c as _c } from "react/compiler-runtime";
import type { ComponentType, ReactNode } from 'react';
import { ChatDisclaimer } from './chat-disclaimer';
import { ChatInputContainer } from './chat-input';
import { ChatSettingsPanel } from './chat-settings-panel';
import { Messages, type PreviewMessageProps } from './messages';
import { useChatOverlayContext } from '../contexts/chat-overlay-context';
import type { DataPartRenderer, ToolOutputRenderer } from '../lib/default-renderers';
interface ChatContentProps {
  // Pass through all props that Messages component expects
  chatUuid?: string;
  className?: string;
  isLoading?: boolean;
  /**
   * Object mapping tool names to their renderer functions.
   * Keys are tool names (e.g., 'processFiles', 'image_generation').
   * Values are renderer functions that take the part and messageUuid.
   */
  toolRenderers?: Record<string, ToolOutputRenderer>;
  /**
   * Object mapping data part types to their renderer functions.
   * Keys are data part types (e.g., 'data-coverageGapAnalysis').
   * Values are renderer functions that take the part and message.
   */
  dataPartRenderers?: Record<string, DataPartRenderer>;
  /**
   * Custom message renderer component.
   * If provided, this component will be used instead of the default PreviewMessage.
   * Must accept the same props as PreviewMessage.
   */
  messageRenderer?: ComponentType<PreviewMessageProps>;
  /**
   * Controls whether inline citations and sources are rendered.
   * Passed through to Messages component.
   */
  enableSourcesRendering?: boolean;
  // Props for input container
  placeholder?: string;
  toolbar?: ReactNode;
}

/**
 * Content wrapper component that decides between rendering Messages+Input or Settings panel.
 * Keeps Messages component unchanged and focused on its single responsibility.
 *
 * When settings is open, displays only the ChatSettingsPanel.
 * When settings is closed, displays the Messages component with input and disclaimer.
 */
export function ChatContent(t0) {
  const $ = _c(14);
  let messagesProps;
  let placeholder;
  let toolbar;
  if ($[0] !== t0) {
    ({
      placeholder,
      toolbar,
      ...messagesProps
    } = t0);
    $[0] = t0;
    $[1] = messagesProps;
    $[2] = placeholder;
    $[3] = toolbar;
  } else {
    messagesProps = $[1];
    placeholder = $[2];
    toolbar = $[3];
  }
  const {
    isSettingsOpen
  } = useChatOverlayContext();
  if (isSettingsOpen) {
    let t1;
    if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <ChatSettingsPanel />;
      $[4] = t1;
    } else {
      t1 = $[4];
    }
    return t1;
  }
  let t1;
  if ($[5] !== messagesProps) {
    t1 = <div className="relative flex flex-1 flex-col overflow-hidden"><Messages {...messagesProps} /></div>;
    $[5] = messagesProps;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== placeholder || $[8] !== toolbar) {
    t2 = <ChatInputContainer placeholder={placeholder} toolbar={toolbar} />;
    $[7] = placeholder;
    $[8] = toolbar;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChatDisclaimer />;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  let t4;
  if ($[11] !== t1 || $[12] !== t2) {
    t4 = <>{t1}{t2}{t3}</>;
    $[11] = t1;
    $[12] = t2;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  return t4;
}